import { useEffect, useState } from "react";
import { Building2, CheckCircle2, XCircle } from "lucide-react";
import { ghostBtn, inputCls } from "@/components/AuthLayout";
import { useAuth } from "@/context/AuthContext";
import { usePrefs } from "@/context/PrefsContext";
import { api, errMsg } from "@/lib/api";

export const PENDING_CODE = "yk_pending_code";
export const PENDING_LEVEL = "yk_pending_level";

export function useInvitationCheck(code) {
  const [st, setSt] = useState({ status: "idle" });
  useEffect(() => {
    const c = (code || "").trim().toUpperCase();
    if (c.length < 4) { setSt({ status: "idle" }); return; }
    let live = true;
    setSt({ status: "checking" });
    const t = setTimeout(() => api.get(`/auth/invitation/${encodeURIComponent(c)}`).then((r) => live && setSt({ status: "ok", inv: r.data })).catch((e) => live && setSt({ status: "bad", msg: errMsg(e) })), 400);
    return () => { live = false; clearTimeout(t); };
  }, [code]);
  return st;
}

export function InvitationStatus({ st, testid = "invitation-status" }) {
  if (st.status === "idle") return null;
  if (st.status === "checking") return <p className="mt-1 text-xs text-muted-foreground" data-testid={testid}>Memeriksa kode…</p>;
  if (st.status === "bad") return <p className="mt-1 inline-flex items-center gap-1 text-xs text-primary" data-testid={testid}><XCircle className="h-3.5 w-3.5" />{st.msg}</p>;
  return <p className="mt-1 inline-flex items-center gap-1 text-xs text-emerald-700" data-testid={testid}><CheckCircle2 className="h-3.5 w-3.5" /><Building2 className="h-3.5 w-3.5" />{st.inv.institution_name}{st.inv.seats_left != null && <span className="text-muted-foreground"> · sisa kursi {st.inv.seats_left}</span>}</p>;
}

export function GoogleButton({ testid = "google-login-button", label, code, level, requireCode = false, showCodeInput = false }) {
  const { googleLogin } = useAuth();
  const { t } = usePrefs();
  const [local, setLocal] = useState("");
  const effective = code ?? local;
  const st = useInvitationCheck(effective);
  const blocked = requireCode ? st.status !== "ok" : (effective.trim().length >= 4 && st.status === "bad");
  const go = () => {
    if (st.status === "ok") { localStorage.setItem(PENDING_CODE, st.inv.code); localStorage.setItem(PENDING_LEVEL, level || st.inv.default_target_level || ""); } else { localStorage.removeItem(PENDING_CODE); localStorage.removeItem(PENDING_LEVEL); }
    googleLogin();
  };
  return (
    <>
      <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground"><span className="h-px flex-1 bg-border" />{t("or")}<span className="h-px flex-1 bg-border" /></div>
      {showCodeInput && (
        <div className="mb-3">
          <input data-testid="google-code-input" className={`${inputCls} uppercase tracking-widest`} value={local} onChange={(e) => setLocal(e.target.value)} placeholder={t("invCode") + " (wajib untuk pengguna Google baru)"} />
          <InvitationStatus st={st} testid="google-code-status" />
        </div>
      )}
      <button data-testid={testid} type="button" onClick={go} disabled={blocked} className={`${ghostBtn} disabled:cursor-not-allowed disabled:opacity-50`}>
        <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.94l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
        {label || t("googleLogin")}
      </button>
      {requireCode && st.status !== "ok" && <p className="mt-1 text-center text-[11px] text-muted-foreground" data-testid="google-need-code">Isi kode batch/lembaga yang valid dulu untuk mengaktifkan tombol Google</p>}
    </>
  );
}
